/**
 * KAN-848: кнопка "Проверить авторизацию" в ячейке статуса. Есть у каждого аккаунта
 * держателя (любой статус, в том числе "Нет авторизации"); у свежей сессии (моложе суток,
 * KAN-661) заблокирована с подсказкой (текстом, если её не показывает строка "Состояние").
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AUTH_CHECK_LABEL,
  AUTH_CHECK_PENDING_LABEL,
  authCheckErrorMessage,
  authResultToast,
  canCheckAuth,
} from '@/lib/telegramAuthCheck';
import { showsHealth } from '@/lib/telegramAccountFormat';
import { errorCode } from '@/lib/telegramProxy';
import {
  FRESH_SESSION_CODE,
  checkAvailableHint,
  freshHoursLeft,
  isFreshSession,
} from '@/lib/telegramSessionAge';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { TelegramAccount } from '@/types/telegramAccount';

export function AccountAuthCheck({
  account,
}: {
  account: TelegramAccount;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const key = `${account.operator_id}-${account.account_id}`;

  const check = useMutation({
    mutationFn: () =>
      telegramAccountsApi.authCheck(account.account_id, account.operator_id),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['telegram-accounts'] });
      const { kind, text } = authResultToast(result);
      if (kind === 'success') toast.success(text);
      else if (kind === 'error') toast.error(text);
      else toast.info(text);
    },
    onError: (err) => {
      if (errorCode(err) === FRESH_SESSION_CODE) {
        void queryClient.invalidateQueries({ queryKey: ['telegram-accounts'] });
      }
      toast.error(authCheckErrorMessage(err));
    },
  });

  if (!canCheckAuth(account)) return null;

  const fresh = isFreshSession(account);
  const hint = fresh ? checkAvailableHint(freshHoursLeft(account)) : '';

  return (
    <div className="mt-1.5">
      <Button
        variant="outline"
        size="sm"
        className="h-7 px-2 text-xs"
        disabled={check.isPending || fresh}
        title={hint || undefined}
        data-testid={`auth-check-${key}`}
        onClick={() => check.mutate()}
      >
        {check.isPending ? AUTH_CHECK_PENDING_LABEL : AUTH_CHECK_LABEL}
      </Button>
      {hint && !showsHealth(account) && (
        <div data-testid={`auth-check-fresh-${key}`} className="mt-0.5 text-xs text-gray-500">
          {hint}
        </div>
      )}
    </div>
  );
}
